import { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MessagesPage({ user }) {
  const [searchParams] = useSearchParams();
  const preselectedUserId = searchParams.get("userId");
  
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (preselectedUserId && conversations.length > 0) {
      const userConvo = conversations.find(c => c.user.id === preselectedUserId);
      if (userConvo) {
        selectConversation(userConvo.user);
      }
    }
  }, [preselectedUserId, conversations]);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem("token");
      const [messagesRes, connectionsRes] = await Promise.all([
        axios.get(`${API}/messages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/connections`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const allMessages = messagesRes.data;
      const connections = connectionsRes.data;

      const userIds = new Set();
      allMessages.forEach(msg => {
        if (msg.sender_id !== user.id) userIds.add(msg.sender_id);
        if (msg.receiver_id !== user.id) userIds.add(msg.receiver_id);
      });

      connections.forEach(conn => {
        if (conn.user_id !== user.id) userIds.add(conn.user_id);
        if (conn.connected_user_id !== user.id) userIds.add(conn.connected_user_id);
      });

      const usersData = await Promise.all(
        Array.from(userIds).map(id =>
          axios.get(`${API}/users/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => res.data)
            .catch(() => null)
        )
      );

      const conversationsMap = {};
      usersData.filter(u => u).forEach(userData => {
        const userMessages = allMessages.filter(
          msg => (msg.sender_id === userData.id && msg.receiver_id === user.id) ||
                 (msg.sender_id === user.id && msg.receiver_id === userData.id)
        );
        const lastMessage = userMessages.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        )[0];

        conversationsMap[userData.id] = {
          user: userData,
          lastMessage: lastMessage || null,
          unreadCount: userMessages.filter(m => !m.read && m.receiver_id === user.id).length
        };
      });

      setConversations(Object.values(conversationsMap).sort((a, b) => {
        if (!a.lastMessage) return 1;
        if (!b.lastMessage) return -1;
        return new Date(b.lastMessage.timestamp) - new Date(a.lastMessage.timestamp);
      }));
    } catch (error) {
      console.error("Failed to fetch conversations", error);
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (userData) => {
    setSelectedUser(userData);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/messages/conversation/${userData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);

      response.data.forEach(msg => {
        if (msg.receiver_id === user.id && !msg.read) {
          axios.put(`${API}/messages/${msg.id}/read`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      });
    } catch (error) {
      console.error("Failed to fetch conversation", error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/messages`,
        { receiver_id: selectedUser.id, content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages([...messages, response.data]);
      setNewMessage("");
      fetchConversations();
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const getInitials = (name) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase();
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk', color: '#4ECDC4' }}>
          Messages
        </h1>
        <p className="text-gray-600">Connect with fellow graduates and students</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[600px]">
        <Card className="md:col-span-1" data-testid="conversations-list">
          <CardHeader className="border-b">
            <CardTitle className="text-lg">Conversations</CardTitle>
          </CardHeader>
          <ScrollArea className="h-[520px]">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <MessageCircle className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                  <p>No conversations yet</p>
                  <p className="text-sm mt-1">Connect with graduates to start messaging</p>
                </div>
              ) : (
                conversations.map((convo) => (
                  <div
                    key={convo.user.id}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedUser?.id === convo.user.id ? "bg-teal-50" : ""
                    }`}
                    onClick={() => selectConversation(convo.user)}
                    data-testid={`conversation-${convo.user.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)' }}>
                        <AvatarFallback className="text-white font-bold">
                          {getInitials(convo.user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <p className="font-medium truncate">{convo.user.name}</p>
                          {convo.unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-2">
                              {convo.unreadCount}
                            </span>
                          )}
                        </div>
                        {convo.lastMessage && (
                          <p className="text-sm text-gray-500 truncate">
                            {convo.lastMessage.sender_id === user.id ? "You: " : ""}
                            {convo.lastMessage.content}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </ScrollArea>
        </Card>

        <Card className="md:col-span-2" data-testid="message-thread">
          {selectedUser ? (
            <>
              <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-teal-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)' }}>
                      <AvatarFallback className="text-white font-bold">
                        {getInitials(selectedUser.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-lg">{selectedUser.name}</CardTitle>
                      <p className="text-sm text-gray-600">{selectedUser.trade_focus}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`mailto:compliance@ngta.com?subject=Report User: ${selectedUser.name}&body=User ID: ${selectedUser.id}%0D%0A%0D%0APlease describe the issue:`, '_blank')}
                    className="text-red-600 hover:text-red-700 border-red-300"
                    data-testid="report-user-button"
                  >
                    Report
                  </Button>
                </div>
              </CardHeader>
              <ScrollArea className="h-[440px] p-4">
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user.id ? "justify-end" : "justify-start"}`}
                      data-testid={`message-${msg.id}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-4 py-2 ${
                          msg.sender_id === user.id
                            ? "bg-gradient-to-r from-orange-500 to-teal-500 text-white"
                            : "bg-gray-100 text-gray-900"
                        }`}
                      >
                        <p className="break-words">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.sender_id === user.id ? "text-white opacity-80" : "text-gray-500"
                        }`}>
                          {formatDistanceToNow(new Date(msg.timestamp))} ago
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <CardContent className="border-t p-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                    data-testid="message-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim()}
                    data-testid="send-message-button"
                    style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p>Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}